import type { ComponentType } from 'react';
import type { MarketingHubSlug } from '@/lib/routing/navTree';
import { IllustrationSvg, type IllustrationSvgProps } from './IllustrationSvg';
import { HubHeroIllustration } from './hubIllustrations';
import { LeafHeroIllustration } from './leafIllustrations';
import { normalizeIllustrationPath } from './illustrationUtils';

type CategoryIllustrationProps = Omit<IllustrationSvgProps, 'idPrefix' | 'children'>;

function ServicesFullCycleIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-services-full-cycle" {...props}>
      <circle cx="200" cy="140" r="52" stroke="url(#cat-services-full-cycle-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M200 104v72M164 140h72" stroke="url(#cat-services-full-cycle-grad)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M120 200h160" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.35" />
      <rect x="128" y="72" width="48" height="36" rx="8" stroke="var(--color-secondary)" strokeWidth="1.8" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <rect x="224" y="72" width="48" height="36" rx="8" stroke="var(--color-primary)" strokeWidth="1.8" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
    </IllustrationSvg>
  );
}

function ServicesAdvancedTechIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-services-advanced" {...props}>
      <rect x="108" y="88" width="184" height="104" rx="16" stroke="url(#cat-services-advanced-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.65" />
      <path d="M136 120h48v32h-48zM216 112h48v40h-48z" stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.12" />
      <path d="M96 208h208" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.45" />
      <circle cx="200" cy="152" r="10" fill="url(#cat-services-advanced-grad)" />
      <path d="M200 152l40-24M200 152l-40-24" stroke="url(#cat-services-advanced-grad)" strokeWidth="2" strokeLinecap="round" />
    </IllustrationSvg>
  );
}

function TechnologiesCategoryIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-technologies" {...props}>
      <path d="M112 108h176v88H112z" stroke="url(#cat-technologies-grad)" strokeWidth="2.5" rx="12" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M136 132h48M136 156h96" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
      <path d="M88 196c48-32 112-32 160 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.4" />
      <circle cx="128" cy="196" r="8" fill="var(--color-primary)" />
      <circle cx="200" cy="172" r="8" fill="var(--color-secondary)" />
      <circle cx="272" cy="196" r="8" fill="var(--color-primary)" />
      <rect x="248" y="80" width="56" height="40" rx="8" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.85" />
    </IllustrationSvg>
  );
}

function AiSolutionsIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-ai-solutions" {...props}>
      <rect x="120" y="96" width="160" height="96" rx="14" stroke="url(#cat-ai-solutions-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M152 128h96M152 152h64" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="200" cy="72" r="16" fill="var(--color-primary)" fillOpacity="0.2" stroke="var(--color-primary)" strokeWidth="2" />
      <path d="M88 200h224" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.3" />
      <path d="M248 184l24-32 24 20" stroke="url(#cat-ai-solutions-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </IllustrationSvg>
  );
}

function AiTrustIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-ai-trust" {...props}>
      <path d="M200 88l48 24v48l-48 24-48-24v-48l48-24z" stroke="url(#cat-ai-trust-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M184 148l16 16 32-40" stroke="url(#cat-ai-trust-grad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="108" y="196" width="184" height="12" rx="6" fill="var(--color-primary)" fillOpacity="0.15" />
      <rect x="108" y="196" width="120" height="12" rx="6" fill="url(#cat-ai-trust-grad)" fillOpacity="0.45" />
    </IllustrationSvg>
  );
}

function AiGeneralIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-ai-general" {...props}>
      <circle cx="200" cy="132" r="40" stroke="url(#cat-ai-general-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <path d="M152 176c16-20 48-20 64 0M120 120l24 12M256 120l-24 12" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.5" />
      <circle cx="120" cy="120" r="10" fill="var(--color-secondary)" fillOpacity="0.5" />
      <circle cx="280" cy="120" r="10" fill="var(--color-secondary)" fillOpacity="0.5" />
    </IllustrationSvg>
  );
}

function HireEngagementIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-hire-engagement" {...props}>
      <circle cx="152" cy="148" r="28" stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-primary)" fillOpacity="0.12" />
      <circle cx="248" cy="148" r="28" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.12" />
      <path d="M140 136c4-10 20-10 24 0M236 136c4-10 20-10 24 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" />
      <path d="M120 200h160" stroke="url(#cat-hire-engagement-grad)" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="156" y="80" width="88" height="40" rx="10" stroke="url(#cat-hire-engagement-grad)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
    </IllustrationSvg>
  );
}

function HireDevelopersIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-hire-devs" {...props}>
      <rect x="104" y="88" width="192" height="112" rx="14" stroke="url(#cat-hire-devs-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <path d="M132 120h48v24h-48zM220 112h48v32h-48z" stroke="var(--color-primary)" strokeWidth="2" fill="var(--color-secondary)" fillOpacity="0.1" />
      <path d="M128 168h144" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
      <path d="M160 200h80" stroke="url(#cat-hire-devs-grad)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.5" />
    </IllustrationSvg>
  );
}

function IndustriesSectorIllustration(props: CategoryIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="cat-industries" {...props}>
      <path d="M96 200V128h48v72M168 200V104h48v96M240 200v-56h48v56" stroke="url(#cat-industries-grad)" strokeWidth="2.5" strokeLinecap="round" fill="var(--semantic-bg-secondary)" fillOpacity="0.55" />
      <path d="M88 200h224" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="304" cy="96" r="24" stroke="var(--color-secondary)" strokeWidth="2" fill="url(#cat-industries-grad)" fillOpacity="0.18" />
      <path d="M296 96h16M304 88v16" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
    </IllustrationSvg>
  );
}

function PortfolioLaneIllustration({ lane, ...props }: CategoryIllustrationProps & { lane: 'studies' | 'platforms' }) {
  const id = lane === 'studies' ? 'cat-portfolio-studies' : 'cat-portfolio-platforms';
  return (
    <IllustrationSvg idPrefix={id} {...props}>
      <rect x="96" y="100" width="96" height="112" rx="12" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <rect x="208" y="116" width="96" height="96" rx="12" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      {lane === 'studies' ? (
        <path d="M120 132h48M120 152h32M232 140h48" stroke={`url(#${id}-grad)`} strokeWidth="2" strokeLinecap="round" strokeOpacity="0.6" />
      ) : (
        <path d="M232 140h48M232 160h32M120 132h32" stroke={`url(#${id}-grad)`} strokeWidth="2" strokeLinecap="round" strokeOpacity="0.6" />
      )}
      <circle cx="200" cy="220" r="20" fill={`url(#${id}-grad)`} fillOpacity="0.2" stroke={`url(#${id}-grad)`} strokeWidth="2" />
    </IllustrationSvg>
  );
}

const CATEGORY_BY_PATH: Record<string, ComponentType<CategoryIllustrationProps>> = {
  'services/full-cycle': ServicesFullCycleIllustration,
  'services/advanced-tech': ServicesAdvancedTechIllustration,
  'technologies/frontend': TechnologiesCategoryIllustration,
  'technologies/backend': TechnologiesCategoryIllustration,
  'technologies/mobile': TechnologiesCategoryIllustration,
  'technologies/cloud': TechnologiesCategoryIllustration,
  'technologies/erp-crm': TechnologiesCategoryIllustration,
  'technologies/data-ai-ml': TechnologiesCategoryIllustration,
  'technologies/devops-data': TechnologiesCategoryIllustration,
  'ai-agents/solutions': AiSolutionsIllustration,
  'ai-agents/trust': AiTrustIllustration,
  'portfolio/case-studies': (p) => <PortfolioLaneIllustration lane="studies" {...p} />,
  'portfolio/platforms': (p) => <PortfolioLaneIllustration lane="platforms" {...p} />,
};

const HIRE_ENGAGEMENT_SLUGS = new Set([
  'dedicated-developers',
  'staff-augmentation',
  'offshore-developers',
  'dedicated-teams',
]);

const HIRE_DEV_SLUGS = new Set(['web-developers', 'mobile-developers', 'other-engineers', 'data-ai-automation']);

function resolveAiCategoryIllustration(category: string): ComponentType<CategoryIllustrationProps> {
  if (category === 'solutions' || category === 'strategy' || category === 'generative-ai') {
    return AiSolutionsIllustration;
  }
  if (category === 'trust' || category.includes('responsible') || category.includes('compliance') || category.includes('security') || category.includes('privacy')) {
    return AiTrustIllustration;
  }
  return AiGeneralIllustration;
}

export function CategoryHeroIllustration({
  path,
  ...props
}: CategoryIllustrationProps & { path: string }) {
  const normalized = normalizeIllustrationPath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments.length >= 3) {
    return <LeafHeroIllustration path={normalized} {...props} />;
  }
  if (segments.length < 2) {
    return <HubHeroIllustration hub={(segments[0] ?? 'services') as MarketingHubSlug} {...props} />;
  }

  const categoryKey = `${segments[0]}/${segments[1]}`;
  const Exact = CATEGORY_BY_PATH[categoryKey];
  if (Exact) return <Exact {...props} />;

  const hub = segments[0] as MarketingHubSlug;
  const category = segments[1] ?? '';

  if (hub === 'hire') {
    if (HIRE_DEV_SLUGS.has(category)) return <HireDevelopersIllustration {...props} />;
    if (HIRE_ENGAGEMENT_SLUGS.has(category)) return <HireEngagementIllustration {...props} />;
    return <HireEngagementIllustration {...props} />;
  }
  if (hub === 'ai-agents') {
    const Component = resolveAiCategoryIllustration(category);
    return <Component {...props} />;
  }
  if (hub === 'industries') {
    return <IndustriesSectorIllustration {...props} />;
  }
  if (hub === 'services') {
    return category.includes('advanced') ? (
      <ServicesAdvancedTechIllustration {...props} />
    ) : (
      <ServicesFullCycleIllustration {...props} />
    );
  }
  if (hub === 'technologies') {
    return <TechnologiesCategoryIllustration {...props} />;
  }
  if (hub === 'portfolio') {
    return <PortfolioLaneIllustration lane="studies" {...props} />;
  }

  return <LeafHeroIllustration path={normalized} {...props} />;
}
